import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@curves/ui-base";
import useShowPDF from "@util/hook/useShowPDF";
import useDocMembershipTransferInApi from "@apis/useDocMembershipTransferInApi";
import MembershipTransferInOrigingMembership from "@icoach/documents/membership/transferIn/MembershipTransferInOrigingMembership";
import MembershipTransferInModifyMembership from "@icoach/documents/membership/transferIn/MembershipTransferInModifyMembership";
import MembershipTransferInFee from "@icoach/documents/membership/transferIn/MembershipTransferInFee";
import MembershipTransferInMemberProfile from "@icoach/documents/membership/transferIn/MembershipTransferInMemberProfile";
import MembershipTransferInPaymentAuthorization from "@icoach/documents/membership/transferIn/MembershipTransferInPaymentAuthorization";
import { DocumentStepper } from "@icoach/documents/components/";
import { mapStepsWithErrorState } from "@icoach/documents/tools";
import useValidationErrors from "@util/hook/useValidationErrors";
import { isEmpty, isEqual } from "@util/utils";
import { TARGET_TYPE } from "@constants/";
import useInactivityTimerToggle from "@util/hook/useInactivityTimerToggle";
import { FocusModeToggle } from "@icoach/components/";
import EnhanceCyclePurchasePromptDialog from "@icoach/documents/membership/dialog/EnhanceCyclePurchasePromptDialog";
import useToggleDialog from "@util/hook/useToggleDialog";
import MembershipTransferInSurvey from "@icoach/documents/membership/transferIn/MembershipTransferInSurvey";
import useMembershipStepper from "@icoach/documents/membership/hooks/useMembershipStepper";
import { resolveTransferInSurveyState } from "@icoach/documents/membership/transferIn/transferInFormRules";
import useMessageDialog from "@util/hook/useMessageDialog";
import { MembershipProvider } from "@icoach/documents/membership/MembershipContext";

// 移入申請書填寫步驟資料來源
const tabsStepSourceData = [
	{
		id: "program",
		label: "原會籍",
		content: <MembershipTransferInOrigingMembership />,
		keyMapping: [],
	},
	{
		id: "reProgram",
		label: "會籍調整",
		content: <MembershipTransferInModifyMembership />,
		keyMapping: ["employeeID", "applyDate", "memberType", "paymentType", "membershipTerm", "startDate", "endDate", "joinFee", "monthlyFee", "notes"],
	},
	{
		id: "contractSurvey",
		label: "會員須知",
		content: <MembershipTransferInSurvey />,
		keyMapping: ["items", "isConfirm", "signImg"],
	},
	{
		id: "payment",
		label: "初次費用",
		content: <MembershipTransferInFee />,
		keyMapping: ["expectedFirstMonthFee", "cardPaymentAmount", "cashPaymentAmount", "isImmediatelyIssuing", "firstCheckContract", "firstSign"],
	},
	{
		id: "basic",
		label: "基本資料",
		content: <MembershipTransferInMemberProfile />,
		keyMapping: [
			"name",
			"nickName",
			"identifierID",
			"birthday",
			"guardianSign",
			"email",
			"cellPhone",
			"tel",
			"companyTel",
			"postCode",
			"address",
			"contactName",
			"contactPhone",
			"contactship",
			"memberCardNo",
		],
	},
	{
		id: "paymentAuthorization",
		label: "扣款授權",
		content: <MembershipTransferInPaymentAuthorization />,
		keyMapping: [
			"paymentCardSign",
			"paymentCreditCardBankName",
			"paymentCreditCardType",
			"paymentCreditCardNo",
			"paymentCardExpiryDate",
			"paymentCardName",
			"paymentCardSign",
			"paymentBankCode",
			"paymentBankName",
			"paymentBankAccount",
			"paymentBankAccountName",
			"paymentBankAccountID",
			"bankCardSignImg",
			"invoiceCarrierType",
			"invoiceIssuingType",
			"invoiceCarrierNo",
			"invoiceTitle",
			"invoiceTaxID",
			"invoiceLoveCode",
		],
	},
];

// 「會員須知」是第幾步。用 id 找而不是寫死數字，之後步驟順序調動不會失效。
const SURVEY_STEP_INDEX = tabsStepSourceData.findIndex(item => item.id === "contractSurvey");

const MembershipTransferInContent = props => {
	const { onReturnHistory, isReadOnly } = props;
	const [options, setOptions] = useState({});
	const [isMounted, setMounted] = useState(false);
	const { openTarget, handleOpenDialog, handleCloseDialog, ...sourceData } = useToggleDialog();
	const setMessageDialog = useMessageDialog();
	const { validationErrors, updateValidationErrors, clearValidationErrors } = useValidationErrors();
	const { peopleType: peopleTypeProps, referenceID: referenceIDProps, contractID: contractIDProps } = useParams();
	const showPDF = useShowPDF();
	const { getDocTransferInContractApi, postDocTransferInContractApi, postDocTemporaryTransferInContractApi } = useDocMembershipTransferInApi();
	const temporarySourceDataRef = useRef({});
	const apiSendingRef = useRef(false);
	const {
		isCompleteContract, // 是否為正式文件
	} = temporarySourceDataRef.current;
	const { currentStep, contentRef, goToStep, getCurrentStepResult } = useMembershipStepper({
		stepCount: tabsStepSourceData.length,
		surveyStepIndex: SURVEY_STEP_INDEX,
		isReadOnly,
		// 移入的資料由子元件的 setParams() 直接寫進 ref，
		// hook 會先呼叫 getResult() 再呼叫這裡，所以讀 ref 拿到的就是最新值。
		readSurveyState: () => resolveTransferInSurveyState(temporarySourceDataRef.current),
		onBlocked: () => setMessageDialog({
			open: true,
			title: "提醒",
			msg: "需讀取會員須知及個資說明方可填寫會員資訊",
			buttonText: "我知道了",
			buttonColor: "secondary",
		}),
	});
	const focusModeRef = useRef(false);

	const checkStepsForErrors = useCallback(mapStepsWithErrorState, [JSON.stringify(validationErrors)]);

	useInactivityTimerToggle();

	// 確認送出
	const handleSubmitClick = () => {
		if (apiSendingRef.current) return false;
		clearValidationErrors();
		getCurrentStepResult();
		postTransferInDataApi(temporarySourceDataRef.current);
	};

	// 暫存
	const handleTemporaryClick = () => {
		if (apiSendingRef.current) return false;
		getCurrentStepResult();
		apiSendingRef.current = true;
		postTemporaryStorageApi(temporarySourceDataRef.current);
	};

	const redirectToPage = contractID => {
		window.setTimeout(() => {
			onReturnHistory && onReturnHistory("14", contractID);
		}, 5);
	};

	// 10: 移入會員 11: 手動移入 13: 未完成移入 14:已移入會員
	// 送出 移入申請書
	const postTransferInDataApi = async params => {
		apiSendingRef.current = true;
		const resp = await postDocTransferInContractApi(contractIDProps, { ...params });
		const { isError = false, result, completedPath, hasCyclePurchase, memberID, contractID } = resp || {};

		if (isError) {
			// 0002 錯誤
			updateValidationErrors(result);
		} else if (isEmpty(resp)) {
			// 失敗
		} else {
			// 成功
			if (hasCyclePurchase) handleOpenDialog("cycle-purchase", { memberID, contractID });
			else redirectToPage(contractID);

			showPDF({
				open: true,
				pdfUrl: completedPath,
			});
		}
		apiSendingRef.current = false;
	};

	// 暫存資料
	const postTemporaryStorageApi = async params => {
		const resp = await postDocTemporaryTransferInContractApi({ ...params });
		if (resp) {
			window.setTimeout(() => {
				onReturnHistory && onReturnHistory(TARGET_TYPE.TRANSFERTEMPORARY, resp);
			}, 5);
		} else {
			apiSendingRef.current = false;
		}
	};

	const doInitTransferInDataApi = async () => {
		const resp = await getDocTransferInContractApi(contractIDProps, {
			type: peopleTypeProps,
			id: referenceIDProps,
		});
		if (resp) {
			const { resource, ...others } = resp;
			temporarySourceDataRef.current = { ...others };
			setOptions(resource);
			setMounted(true);
		}
	};

	useEffect(
		() => {
			doInitTransferInDataApi();
			return () => setMounted(false);
		},
		// eslint-disable-next-line
		[],
	);

	return (
		<React.Fragment>
			{isMounted && (
				<FocusModeToggle ref={focusModeRef} showToggleFocusModeButton={!isReadOnly} defaultFocusMode={!isReadOnly}>
					<Card>
						<Card.CardContent>
							<MembershipProvider value={{ temporarySourceDataRef, options, isReadOnly, errorModule: validationErrors }}>
								<DocumentStepper
									steps={checkStepsForErrors(tabsStepSourceData, validationErrors)}
									currentStep={currentStep}
									contentRef={contentRef}
									onStepChange={goToStep}
									onSubmit={() => focusModeRef.current.checkAndExitFocusMode(handleSubmitClick)}
									onTemporary={() => focusModeRef.current.checkAndExitFocusMode(handleTemporaryClick)}
									onCancel={() => onReturnHistory && onReturnHistory(peopleTypeProps, 0)}
									isTemporary={!isReadOnly && !isCompleteContract}
									isSubmit={!isReadOnly}
								/>
							</MembershipProvider>
						</Card.CardContent>
					</Card>
					<EnhanceCyclePurchasePromptDialog open={isEqual(openTarget, "cycle-purchase")} onClose={handleCloseDialog} redirectToPage={redirectToPage} sourceData={sourceData} />
				</FocusModeToggle>
			)}
		</React.Fragment>
	);
};
export default MembershipTransferInContent;
